
import * as MenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { Link } from "./router";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";

import { cn } from "./cn";
import styles from "./overlay.module.css";

export const Menu = MenuPrimitive.Root;
export const MenuTrigger = MenuPrimitive.Trigger;

export const MenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Content>
>(function MenuContent({ className, align = "end", sideOffset = 6, ...rest }, ref) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(styles.menu, className)}
        {...rest}
      />
    </MenuPrimitive.Portal>
  );
});

export type MenuItemProps = ComponentPropsWithoutRef<typeof MenuPrimitive.Item> & {
  tone?: "neutral" | "danger";
  icon?: ReactNode;
  href?: string;
  download?: boolean | string;
  target?: string;
  rel?: string;
};

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
  { tone = "neutral", icon, href, download, target, rel, className, children, ...rest },
  ref,
) {
  const body = (
    <>
      {icon}
      <span className={styles.menuItemText}>{children}</span>
    </>
  );

  if (href) {
    return (
      <MenuPrimitive.Item
        ref={ref}
        asChild
        className={cn(styles.menuItem, styles.menuItemLink, tone === "danger" && styles.menuItemDanger, className)}
        {...rest}
      >
        <Link href={href} download={download} target={target} rel={rel}>
          {body}
        </Link>
      </MenuPrimitive.Item>
    );
  }

  return (
    <MenuPrimitive.Item
      ref={ref}
      className={cn(styles.menuItem, tone === "danger" && styles.menuItemDanger, className)}
      {...rest}
    >
      {body}
    </MenuPrimitive.Item>
  );
});

export const MenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Label>
>(function MenuLabel({ className, ...rest }, ref) {
  return <MenuPrimitive.Label ref={ref} className={cn(styles.menuLabel, className)} {...rest} />;
});

export const MenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Separator>
>(function MenuSeparator({ className, ...rest }, ref) {
  return <MenuPrimitive.Separator ref={ref} className={cn(styles.menuSeparator, className)} {...rest} />;
});

export const MenuRadioGroup = MenuPrimitive.RadioGroup;

export const MenuRadioItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem>
>(function MenuRadioItem({ className, children, ...rest }, ref) {
  return (
    <MenuPrimitive.RadioItem ref={ref} className={cn(styles.menuItem, className)} {...rest}>
      <span className={styles.menuItemText}>{children}</span>
      <MenuPrimitive.ItemIndicator className={styles.menuCheck}>
        <Check size={14} strokeWidth={1.75} aria-hidden="true" />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.RadioItem>
  );
});

export const MenuCheckboxItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>
>(function MenuCheckboxItem({ className, children, ...rest }, ref) {
  return (
    <MenuPrimitive.CheckboxItem ref={ref} className={cn(styles.menuItem, className)} {...rest}>
      <span className={styles.menuItemText}>{children}</span>
      <MenuPrimitive.ItemIndicator className={styles.menuCheck}>
        <Check size={14} strokeWidth={1.75} aria-hidden="true" />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.CheckboxItem>
  );
});
